/**
 * RTK Query slice for the agent domain (design §20). Mirrors the P1 feature
 * pattern: `apiSlice.enhanceEndpoints({ addTagTypes }).injectEndpoints`, relative
 * urls under `agent/…` (the vite `/api/agent` proxy → agent-factory :3610),
 * `{ data: … }` envelope unwrapping. The wizard's post-create attach sequence
 * drives the attach mutations imperatively (sequential awaits with per-item
 * state) — progress is UI state, not cache state (§15.3).
 */
import { apiSlice } from '@/store/apiSlice';

import type {
  AgentFactoryView,
  AgentTemplate,
  AttachPoliciesResult,
  CreateFactoryTriggerBody,
  FromTemplateBody,
  FromTemplateResult,
  MemoryAttachResult,
  ModelCollection,
  RevokePolicyResult,
  TemplatePolicySpec,
  TriggerBindingView,
} from '../types';

interface DataEnvelope<T> {
  data?: T;
}

function unwrap<T>(raw: DataEnvelope<T> | T | undefined): T | undefined {
  if (raw && typeof raw === 'object' && 'data' in (raw as DataEnvelope<T>)) {
    return (raw as DataEnvelope<T>).data;
  }
  return raw as T | undefined;
}

const agentsTaggedApi = apiSlice.enhanceEndpoints({
  addTagTypes: ['Templates', 'Template', 'ModelCollections', 'FactoryList', 'Factory', 'Triggers'],
});

export interface GetTemplatesArgs {
  kind?: string;
}

export interface GetFactoriesArgs {
  orgId?: string;
}

export interface UpdateFactoryArgs {
  id: string;
  changes: {
    name?: string;
    instructions?: string;
    isActive?: boolean;
    tags?: string[];
  };
}

export interface AttachPoliciesArgs {
  id: string;
  policySpecs: TemplatePolicySpec[];
}

export interface RevokePolicyArgs {
  id: string;
  policyId: string;
}

export interface CreateTriggerArgs {
  id: string;
  body: CreateFactoryTriggerBody;
}

export interface DeleteTriggerArgs {
  id: string;
  bindingId: string;
}

export const agentsApi = agentsTaggedApi.injectEndpoints({
  endpoints: (builder) => ({
    getTemplates: builder.query<AgentTemplate[], GetTemplatesArgs | void>({
      query: (args) => ({
        url: 'agent/templates',
        params: (args as GetTemplatesArgs | undefined)?.kind
          ? { kind: (args as GetTemplatesArgs).kind }
          : undefined,
      }),
      transformResponse: (raw: DataEnvelope<AgentTemplate[]> | AgentTemplate[]) =>
        unwrap<AgentTemplate[]>(raw) ?? [],
      providesTags: [{ type: 'Templates', id: 'LIST' }],
    }),

    getTemplate: builder.query<AgentTemplate, string>({
      query: (id) => ({ url: `agent/templates/${id}` }),
      transformResponse: (raw: DataEnvelope<AgentTemplate>) =>
        unwrap<AgentTemplate>(raw) as AgentTemplate,
      providesTags: (_result, _error, id) => [{ type: 'Template', id }],
    }),

    getModelCollections: builder.query<ModelCollection[], void>({
      query: () => ({ url: 'agent/model-collections' }),
      transformResponse: (raw: DataEnvelope<ModelCollection[]> | ModelCollection[]) =>
        unwrap<ModelCollection[]>(raw) ?? [],
      providesTags: [{ type: 'ModelCollections', id: 'LIST' }],
    }),

    getFactories: builder.query<AgentFactoryView[], GetFactoriesArgs | void>({
      query: (args) => ({
        url: 'agent/factories',
        params: (args as GetFactoriesArgs | undefined)?.orgId
          ? { orgId: (args as GetFactoriesArgs).orgId }
          : undefined,
      }),
      transformResponse: (raw: DataEnvelope<AgentFactoryView[]> | AgentFactoryView[]) =>
        unwrap<AgentFactoryView[]>(raw) ?? [],
      providesTags: [{ type: 'FactoryList', id: 'LIST' }],
    }),

    getFactory: builder.query<AgentFactoryView, string>({
      query: (id) => ({ url: `agent/factories/${id}` }),
      transformResponse: (raw: DataEnvelope<AgentFactoryView>) =>
        unwrap<AgentFactoryView>(raw) as AgentFactoryView,
      providesTags: (_result, _error, id) => [{ type: 'Factory', id }],
    }),

    updateFactory: builder.mutation<AgentFactoryView, UpdateFactoryArgs>({
      query: ({ id, changes }) => ({
        url: `agent/factories/${id}`,
        method: 'PATCH',
        body: changes,
      }),
      transformResponse: (raw: DataEnvelope<AgentFactoryView>) =>
        unwrap<AgentFactoryView>(raw) as AgentFactoryView,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Factory', id },
        { type: 'FactoryList', id: 'LIST' },
      ],
    }),

    createFromTemplate: builder.mutation<FromTemplateResult, FromTemplateBody>({
      query: (body) => ({ url: 'agent/factories/from-template', method: 'POST', body }),
      transformResponse: (raw: DataEnvelope<FromTemplateResult>) =>
        unwrap<FromTemplateResult>(raw) as FromTemplateResult,
      invalidatesTags: [{ type: 'FactoryList', id: 'LIST' }],
    }),

    attachMemory: builder.mutation<MemoryAttachResult, { id: string }>({
      query: ({ id }) => ({ url: `agent/factories/${id}/memory`, method: 'POST' }),
      transformResponse: (raw: DataEnvelope<MemoryAttachResult>) =>
        unwrap<MemoryAttachResult>(raw) as MemoryAttachResult,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Factory', id },
        { type: 'FactoryList', id: 'LIST' },
      ],
    }),

    detachMemory: builder.mutation<MemoryAttachResult, { id: string }>({
      query: ({ id }) => ({ url: `agent/factories/${id}/memory`, method: 'DELETE' }),
      transformResponse: (raw: DataEnvelope<MemoryAttachResult>) =>
        unwrap<MemoryAttachResult>(raw) as MemoryAttachResult,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Factory', id },
        { type: 'FactoryList', id: 'LIST' },
      ],
    }),

    attachPolicies: builder.mutation<AttachPoliciesResult, AttachPoliciesArgs>({
      query: ({ id, policySpecs }) => ({
        url: `agent/factories/${id}/policies`,
        method: 'POST',
        body: { policySpecs },
      }),
      transformResponse: (raw: DataEnvelope<AttachPoliciesResult>) =>
        unwrap<AttachPoliciesResult>(raw) as AttachPoliciesResult,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Factory', id }],
    }),

    revokePolicy: builder.mutation<RevokePolicyResult, RevokePolicyArgs>({
      query: ({ id, policyId }) => ({
        url: `agent/factories/${id}/policies/${policyId}`,
        method: 'DELETE',
      }),
      transformResponse: (raw: DataEnvelope<RevokePolicyResult>) =>
        unwrap<RevokePolicyResult>(raw) as RevokePolicyResult,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Factory', id }],
    }),

    getTriggers: builder.query<TriggerBindingView[], { id: string }>({
      query: ({ id }) => ({ url: `agent/factories/${id}/triggers` }),
      transformResponse: (raw: DataEnvelope<TriggerBindingView[]> | TriggerBindingView[]) =>
        unwrap<TriggerBindingView[]>(raw) ?? [],
      providesTags: (_result, _error, { id }) => [{ type: 'Triggers', id }],
    }),

    createTrigger: builder.mutation<TriggerBindingView, CreateTriggerArgs>({
      query: ({ id, body }) => ({
        url: `agent/factories/${id}/triggers`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw: DataEnvelope<TriggerBindingView>) =>
        unwrap<TriggerBindingView>(raw) as TriggerBindingView,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Triggers', id },
        { type: 'Factory', id },
      ],
    }),

    deleteTrigger: builder.mutation<unknown, DeleteTriggerArgs>({
      // 204 No Content on success.
      query: ({ id, bindingId }) => ({
        url: `agent/factories/${id}/triggers/${bindingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Triggers', id },
        { type: 'Factory', id },
      ],
    }),
  }),
});

export const {
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useGetModelCollectionsQuery,
  useGetFactoriesQuery,
  useGetFactoryQuery,
  useUpdateFactoryMutation,
  useCreateFromTemplateMutation,
  useAttachMemoryMutation,
  useDetachMemoryMutation,
  useAttachPoliciesMutation,
  useRevokePolicyMutation,
  useGetTriggersQuery,
  useCreateTriggerMutation,
  useDeleteTriggerMutation,
} = agentsApi;
